/* eslint-disable no-param-reassign */
import store from 'redux-v2/store';
import { DefaultFilterTypes, breakpoints } from 'components/feature/Report/ReportSidebar/common/constants';
import Cache from 'core/cache/cache';
import CacheItem from 'core/constants/cache';
import { RENAME_DELIMITER, FeatureList, routePaths } from 'core/constants/common';
import { DatePayloadFormat, IDateRangeTypes } from 'core/constants/date';
import {
  FilterType, LinkedFilterOrigin, LinkedFilterSource,
} from 'core/constants/filter';
import {
  DimensionNoValue,
  DimensionNullValue,
  DimensionNumericNoValue,
  DimensionStatus, QueryParamNames, ShowToastTypes,
} from 'core/constants/report';
import { ContextTypes, ListingSectionId, SectionItemId } from 'core/constants/report-listing';
import {
  ObjModel,
} from 'core/models';
import { Obj } from 'core/models/obj';
import {
  IColumn,
  ICustomFilterDefinition,
  IFilterConfig,
  ILinkedFilterInfo,
} from 'core/models/report-response';
import { DeviceType, MobileAuthInfo } from 'core/models/store';
import FeatureDefinitions from 'features/featureDefinitions';
import moment from 'moment-timezone';
import {
  IBuilder,
  IRawFilterMetaInfo,
  IReportConfig,
} from 'core/models/report-builder/report-builder';
import {
  filterBuilderToFilterConfig,
} from 'redux-v2/report-builder/report-builder-store.utils';
import { IFilterMetaInfo } from 'redux-v2/report-builder/report-builder-store.state';
import { SourceTypes } from 'core/models/auth-response';
import { isValidArrayAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';

export const clearAuthCacheItems = () => {
  Cache.removeItem(CacheItem.Auth);
  Cache.removeItem(CacheItem.AuthExpiry);
  Cache.removeItem(CacheItem.UserAttr);
  Cache.removeItem(CacheItem.OrgAttr);
};
export const isV1ReportsContext = () => {
  const { isV1Report } = store.getState().auth;
  return isV1Report;
};

export const isMarvinContext = () => {
  const { isMarvin } = store.getState().auth;
  return isMarvin;
};

export const isEmbeddedContext = () => {
  const { isEmbedded } = store.getState().auth;
  return isEmbedded;
};

export const getPendoState = () => {
  const selectedPage = store.getState().globalStore;
  return selectedPage;
};

export const redirctToLSQReportListingPage = () => {
  const lsqReportsHomePage = process.env.REACT_APP_LSQ_REPORTS_HOME;
  window.open(lsqReportsHomePage, '_self');
};

export const enum ToastNotificationTypes {
  CopyEmbedURLSuccess = 'CopyEmbedURLSuccess',
  RirectionURLGenerationError = 'RirectionURLGenerationError',
  SavedReportsError = 'SavedReportsError',
  ShowProfileCreationSuccess = 'ShowProfileCreationSuccess',
  ShowProfileCreationFailure = 'ShowProfileCreationFailure',
  ShowProfileUpdateSuccess = 'ShowProfileUpdateSuccess',
}

export enum EmptyScreenTypes {
  Pinned = 'pinned',
  PinnedForReport = 'pinnedForReport',
  NoPinnedDashboard = 'noPinnedDashboard',
  AllReports = 'allReports',
  SearchNoReports = 'SearchNoReports',
  SearchDashboards = 'SearchDashboards',
  AllDashboards = 'allDashboards',
  Tags = 'tags',
  HomeDashboard = 'homeDashboards',
  TagsDashboard = 'tagsDashboard',
  NoWidget = 'widget',
  BookmarkedReports = 'bookmarkedReports',
  ScheduledReports = 'scheduledReports',
  ScheduleHistory = 'scheduleHistory',
  ProfileReports = 'profileReports',
  CustomAnalyticsReports = 'customAnalyticsReports',
  CustomAnalyticsDashboards = 'customAnalyticsDashboards',
}

export const removeDuplicateObject = (
  array: any,
  uniqueProperty: any,
  additionalProperty?: any,
) => {
  const uniqueSet = new Set();
  const uniqueArray: any = [];

  array.forEach((object: any) => {
    const uniqueValue = object[uniqueProperty];
    const additionalValue = object[additionalProperty];
    const finalValue = additionalProperty
      ? `${uniqueValue}_${additionalValue || ''}`
      : uniqueValue;

    if (!uniqueSet.has(finalValue)) {
      uniqueSet.add(finalValue);
      uniqueArray.push(object);
    }
  });

  return uniqueArray;
};

export const NoOp = () => {
  // called when no operation has to be performed
};

export const filterDefaultValue: DefaultFilterTypes = {
  [FilterType.DateRange]: {
    Type: IDateRangeTypes.Absolute,
    Value: {
      From: moment().add(-7, 'days').startOf('day').format(DatePayloadFormat),
      To: moment().endOf('day').format(DatePayloadFormat),
    },
  },
  [FilterType.MultiTextSearch]: [],
  [FilterType.UserMultiSelect]: [],
  [FilterType.LSQMetadataMultiSelect]: {},
  [FilterType.GroupMultiSelect]: {},
  [FilterType.NumberInteger]: '',
  [FilterType.NumberDecimal]: '',
};

export const clearPrefix = (input:string, delimiter: string) => {
  const splitInput = input.split(delimiter);
  if (splitInput.length > 1) {
    splitInput.shift();
    return splitInput.join(delimiter);
  }
  return input;
};

export const removeObjectWithSameId = (input: ObjModel.Obj[], key: string) => {
  const result = input.reduce((accumulator, current) => {
    const exists = accumulator.find((item: Obj) => item[key] === current[key]);
    if (!exists) {
      accumulator = accumulator.concat(current);
    }
    return accumulator;
  }, []);

  return result;
};

// Return the Feature Info base on the routePath
export const getFeatureFromPath = (path: routePaths): FeatureList => Object.keys(FeatureDefinitions)?.find((key) => FeatureDefinitions[key].route === path) as FeatureList;

// Returns the context in which application is rendered
export const getCurrentApplicationContext = () => {
  const { isEmbedded, isV1Report, isMarvin } = store.getState().auth;
  switch (true) {
    case isMarvin:
      return ContextTypes.Marvin;
    case isV1Report:
      return ContextTypes.V1;
    case isEmbedded:
      return ContextTypes.Embedded;
    default:
      return ContextTypes.Siera;
  }
};
export const getDeviceType = (width: number): DeviceType => {
  if (width <= breakpoints.MOBILE) {
    return DeviceType.MOBILE;
  }

  if (width <= breakpoints.TABLET) {
    return DeviceType.MOBILE; // treat ipad/tablet as mobile view in terms of UI/UX
  }
  return DeviceType.DESKTOP;
};

export const generateNumberArray = (length: number) => [...new Array(length).keys()];

// Returns the list of all the mapped metafields
// @columns - Array of all applied columns
// @reportData -  report Data for the report.
export const getMappedMetaFields = (columns: IColumn[], reportData:ObjModel.Obj[]) => {
  let referrerCol:IColumn;
  let referredCol:IColumn;
  let mappedMetaFieldsObj:object;
  columns.forEach((col) => {
    if (col.Props.Dimension?.Applied !== DimensionStatus.NotApplied
      && (col.Props.ReferTo !== null && col.Props.ReferTo !== undefined && (col.Props.ReferTo && col.Props.ReferTo !== ''))) {
      referrerCol = col;
      referredCol = columns.find((column) => column?.Id === referrerCol?.Props.ReferTo);
      if (referredCol && referrerCol && reportData) {
        const mappedMetaFieldObj = getMappedMetaField(reportData, referrerCol, referredCol);
        mappedMetaFieldsObj = {
          ...mappedMetaFieldsObj,
          ...mappedMetaFieldObj,
        };
      }
    }
  });

  return mappedMetaFieldsObj;
};

// Returns the applied mapped Metafield
// @referrerCol - Applied parent dimension.
// @fetchdata -  report Data for the report.
// @referrerCol - Applied dimension to which parent dimension linked
export const getMappedMetaField = (fetchdata: ObjModel.Obj[], referrerCol:IColumn, referredCol:IColumn) : object => {
  const referrerColName = referrerCol?.Id;
  let mappedObj:any;
  fetchdata.forEach((data) => {
    let keyString = data[referrerCol?.Id];
    let value = data[referredCol?.Id];
    if (value === '') { // replacing '' with DimensionNoValue
      value = DimensionNoValue;
    }
    if (keyString === '') { // replacing '' with DimensionNoValue
      keyString = DimensionNoValue;
    }
    if (value === DimensionNullValue) {
      value = DimensionNumericNoValue;
    }

    if (keyString === DimensionNullValue) {
      keyString = DimensionNumericNoValue;
    }

    mappedObj = {
      ...mappedObj,
      [keyString]: value,
    };
  });
  return {
    [referrerColName]: mappedObj,
  };
};

// Returns filter config based on the filter group of report raw  config
// @state - Report builder state
export const getFilterConfigFromTemplate = (reportConfig: IReportConfig, filterMetaInfo?: IFilterMetaInfo[], rawFilterMetaInfo?: IRawFilterMetaInfo) => {
  const { Builder, SubBuilders, CustomFilterDefinitions } = reportConfig;
  const filterMetaInformation = filterMetaInfo;
  const partitionFilters = rawFilterMetaInfo?.partitionColumnFilterIds;
  return getFilterConfigFromBuilder(Builder, filterMetaInformation, SubBuilders, partitionFilters, CustomFilterDefinitions);
};

// Reconstructs custom filter entries from constituent filters (those with AssociatedCustomFilterId).
// Replaces separate constituent entries with a single custom filter entry containing ConstituentFilterConfigs.
export const reconstructCustomFilters = (filters: IFilterConfig[]): IFilterConfig[] => {
  const constituentsByCustomId: Record<string, IFilterConfig[]> = {};
  const nonConstituentFilters: IFilterConfig[] = [];
  filters.forEach((filter) => {
    if (filter.AssociatedCustomFilterId) {
      if (!constituentsByCustomId[filter.AssociatedCustomFilterId]) {
        constituentsByCustomId[filter.AssociatedCustomFilterId] = [];
      }
      constituentsByCustomId[filter.AssociatedCustomFilterId].push(filter);
    } else {
      nonConstituentFilters.push(filter);
    }
  });

  const customFilters: IFilterConfig[] = Object.entries(constituentsByCustomId).map(
    ([customFilterId, constituents]) => {
      const firstConstituent = constituents[0];
      return {
        ...firstConstituent,
        ID: customFilterId,
        IsCustomFilter: true,
        CustomFilterType: firstConstituent.Type,
        LinkedFilters: constituents.map((c) => {
          const isQueryAppliedFilter = !!c.QueryId;
          const isQueryAppliedDimension = !isQueryAppliedFilter && !!c.EntityName;
          return {
            FilterId: c.ID,
            Source: isQueryAppliedFilter ? LinkedFilterSource.SubBuilder : LinkedFilterSource.MainReport,
            QueryId: c.QueryId,
            Origin: isQueryAppliedDimension ? LinkedFilterOrigin.AppliedDimension : LinkedFilterOrigin.AppliedFilter,
          };
        }),
        ConstituentFilterConfigs: constituents,
        Alias: customFilterId,
        IsDynamic: true,
      } as IFilterConfig;
    },
  );

  return [...nonConstituentFilters, ...customFilters];
};

/**
 * Returns the DefaultValue for each custom filter whose ALL LinkedFilters are
 * SubBuilder-sourced. The render config does not include SubBuilder constituent
 * filters, so these custom filters are initialized without a DefaultValue.
 * Call this once the raw config is available to resolve the actual defaults.
 *
 * @returns Map of customFilterId → constituent DefaultValue.
 */
export const resolveSubBuilderCustomFilterDefaults = (
  customDefs: ICustomFilterDefinition[],
  rawSubBuilders: IBuilder[],
): Record<string, any> => {
  const resolved: Record<string, any> = {};
  if (!isValidArrayAndNotEmpty(customDefs) || !isValidArrayAndNotEmpty(rawSubBuilders)) {
    return resolved;
  }
  customDefs.forEach((def) => {
    if (!isValidArrayAndNotEmpty(def.LinkedFilters)) return;
    if (!def.LinkedFilters.every((lf) => lf.Source === LinkedFilterSource.SubBuilder)) return;

    const firstLinked = def.LinkedFilters[0];
    // FilterId may already carry the _<QueryId> suffix (SubBuilder filter IDs exist
    // in both bare and suffixed form across the codebase). Append only when missing
    // so the lookup matches raw-config SubBuilder FilterGroup entries regardless of
    // which form was stored on the definition.
    const suffix = `_${firstLinked.QueryId}`;
    const suffixedId = firstLinked.FilterId.endsWith(suffix)
      ? firstLinked.FilterId
      : `${firstLinked.FilterId}${suffix}`;
    rawSubBuilders.some((sb) => {
      const match = (sb.Filter?.FilterGroup || []).find(
        (f: any) => f.ID === suffixedId,
      );
      if (match?.Render?.DefaultValue != null) {
        resolved[def.ID] = match.Render.DefaultValue;
        return true;
      }
      return false;
    });
  });
  return resolved;
};

// Resolves a constituent across main filters and SubBuilders' FilterGroup. Used by
// buildCustomFiltersFromDefinitions to pick the schema source for a custom filter
// wrapper — a constituent picked from a subquery (typical case for FilterKey on a
// dropdown wrapper) doesn't live in main `Filters[]` but does live in
// `SubBuilders[*].Filter.FilterGroup`. When found in SubBuilders, the IFilterGroup
// shape is converted to IFilterConfig by hoisting Render-block fields to the top
// level (mirroring allSubBuilderAppliedFiltersSelector).
const findConstituentInPools = (
  allFilters: IFilterConfig[],
  subBuilders: IBuilder[] | undefined,
  matchPredicate: (filter: IFilterConfig) => boolean,
  groupMatchPredicate: (group: { ID: string; QueryId?: string }, queryId?: string) => boolean,
): IFilterConfig | undefined => {
  const inMain = allFilters.find(matchPredicate);
  if (inMain) return inMain;
  if (!isValidArrayAndNotEmpty(subBuilders)) return undefined;
  for (let i = 0; i < subBuilders.length; i += 1) {
    const queryId = subBuilders[i].QueryId;
    const fg = (subBuilders[i].Filter?.FilterGroup || []).find((g) => groupMatchPredicate(g, queryId));
    if (fg) {
      return {
        ...fg,
        Type: fg.Render?.Type,
        DropdownMetaData: fg.DropdownMetaData || fg.Render?.DropdownMetaData,
        Metadata: fg.Render?.Metadata || (fg as any).Metadata,
      } as IFilterConfig;
    }
  }
  return undefined;
};

// Builds custom filter UI entries from CustomFilterDefinitions.
// Constituents (filters with AssociatedCustomFilterId) remain in the array untouched.
// A new IsCustomFilter entry is created for each definition with metadata from the
// definition. `subBuilders` is consulted when a constituent (especially the FilterKey
// for a dropdown wrapper) is a SubBuilder pick living in a subquery's FilterGroup
// rather than in `allFilters` — without this fallback the wrapper would inherit the
// wrong schema from the first MainReport constituent.
export const buildCustomFiltersFromDefinitions = (
  definitions: ICustomFilterDefinition[],
  allFilters: IFilterConfig[],
  subBuilders?: IBuilder[],
): IFilterConfig[] => {
  const nonConstituentFilters = allFilters.filter((f) => !f.AssociatedCustomFilterId);

  const customFilters: IFilterConfig[] = definitions.map((def) => {
    const linkedIds = def.LinkedFilters.map((lf) => lf.FilterId);
    // Schema source — prefer the FilterKey constituent (drives the dropdown's
    // schema, IsLargeOptionSetField, LinkedTo). Falls back to any linked
    // constituent when no FilterKey is set (date / number / user / non-key
    // dropdowns). SubBuilder FilterGroup IDs may be bare or `_<queryId>`-suffixed,
    // so the group-match predicate accepts either form.
    const filterKey = def.FilterKey;
    const matchById = (id: string) => (f: IFilterConfig) => f.ID === id;
    const matchGroupById = (id: string) => (g: { ID: string }, queryId?: string) => (
      g.ID === id || (!!queryId && (g.ID === `${id}_${queryId}` || `${g.ID}_${queryId}` === id))
    );
    const schemaConstituent = (filterKey
      && findConstituentInPools(allFilters, subBuilders, matchById(filterKey), matchGroupById(filterKey)))
      || findConstituentInPools(
        allFilters,
        subBuilders,
        (f) => linkedIds.includes(f.ID),
        (g, queryId) => linkedIds.some((id) => matchGroupById(id)(g, queryId)),
      );

    const metadata: Record<string, any> = {};
    if (def.UserStatus) metadata.Status = def.UserStatus;
    if (schemaConstituent?.Metadata?.FilterKey) metadata.FilterKey = schemaConstituent.Metadata.FilterKey;

    return {
      ...schemaConstituent,
      ID: def.ID,
      Label: def.Label,
      Type: def.CustomFilterType,
      IsCustomFilter: true,
      CustomFilterType: def.CustomFilterType,
      HideFromReportPage: def.HideFromReportPage,
      LinkedFilters: def.LinkedFilters,
      IsDynamic: true,
      Alias: def.ID,
      Metadata: metadata,
    } as IFilterConfig;
  });

  // Constituents stay in the array (they have AssociatedCustomFilterId set)
  const constituentFilters = allFilters.filter((f) => f.AssociatedCustomFilterId);

  return [...nonConstituentFilters, ...constituentFilters, ...customFilters];
};

// Idempotently appends the SubBuilder QueryId suffix to a linked-filter id for
// payload-key purposes. SubBuilder filter IDs may already carry the suffix in
// LinkedFilterInfo, so append-when-missing avoids producing "<id>_<qid>_<qid>".
const getConstituentPayloadKey = (linkedFilter: ILinkedFilterInfo): string => {
  if (linkedFilter.Source !== LinkedFilterSource.SubBuilder || !linkedFilter.QueryId) {
    return linkedFilter.FilterId;
  }
  const suffix = `_${linkedFilter.QueryId}`;
  return linkedFilter.FilterId.endsWith(suffix) ? linkedFilter.FilterId : `${linkedFilter.FilterId}${suffix}`;
};

// Returns the payload-ready constituent ids for a custom-filter wrapper. Used
// by the dashboard save-time fan-out (persists these ids in GlobalFiltersMapping)
// and by any caller that needs the wrapper's effective constituent keys for
// fetch-report.
export const expandWrapperToConstituentIds = (
  wrapper: { LinkedFilters?: ILinkedFilterInfo[] },
): string[] => {
  if (!isValidArrayAndNotEmpty(wrapper.LinkedFilters)) return [];
  return wrapper.LinkedFilters.map(getConstituentPayloadKey);
};

// Finds the custom-filter wrapper in `filters` whose constituent set matches
// the saved constituent IDs (order-insensitive). Used when a dashboard panel's
// linked target was a wrapper — the persisted value is the array of constituent
// IDs and the UI needs to resolve it back to the wrapper for label display.
// Returns undefined when no exact match exists (e.g. the wrapper definition was
// edited after the dashboard was saved).
export const findWrapperByConstituentIds = (
  filters: IFilterConfig[],
  constituentIds: string[],
): IFilterConfig | undefined => {
  if (!isValidArrayAndNotEmpty(filters) || !isValidArrayAndNotEmpty(constituentIds)) return undefined;
  const targetSet = new Set(constituentIds);
  return filters.find((f) => {
    if (!f.IsCustomFilter) return false;
    const wrapperIds = expandWrapperToConstituentIds(f);
    if (wrapperIds.length !== targetSet.size) return false;
    return wrapperIds.every((id) => targetSet.has(id));
  });
};

export interface IConstituentApplyResult {
  key: string;
  value: any;
  // Top-level FiltersMetadata.UserDropdownSelectType (primary-user constituents)
  userDropdownSelectType?: any;
  // FiltersMetadata.SubBuilderFiltersMetadata entry (sub-builder primary-user constituents)
  subBuilderMetadata?: {
    QueryId: string;
    FilterId: string;
    UserDropdownSelectType: any;
    Status: any;
  };
}

// Applies a custom-filter response to one constituent. Encodes the envelope
// rules: primary user → raw UserIds array (+ metadata propagation); secondary
// user → { DropdownMetaData, Value }; LOS wrapper → non-LOS constituent →
// extract .Values flat; default → pass through unchanged. SubBuilder
// constituents from never-visited subqueries that aren't in main filterConfig
// fall back to a lookup across subBuilders[*].Filter.FilterGroup.
export const applyValueToConstituent = (
  linkedFilter: ILinkedFilterInfo,
  customFilterResponse: any,
  wrapper: { DropdownMetaData?: { UserStatusType?: any } } | undefined,
  isCustomFilterLOS: boolean,
  filterConfig: IFilterConfig[],
  subBuilders?: IBuilder[],
): IConstituentApplyResult => {
  const key = getConstituentPayloadKey(linkedFilter);
  const isSubBuilderConstituent = linkedFilter.Source === LinkedFilterSource.SubBuilder
    && !!linkedFilter.QueryId;

  let constituent: IFilterConfig | undefined = filterConfig.find((f) => f.ID === linkedFilter.FilterId);
  if (!constituent && isValidArrayAndNotEmpty(subBuilders)) {
    const suffixedId = linkedFilter.QueryId
      ? `${linkedFilter.FilterId}_${linkedFilter.QueryId}`
      : linkedFilter.FilterId;
    for (let i = 0; i < subBuilders.length; i += 1) {
      const sbMatch = (subBuilders[i].Filter?.FilterGroup || []).find(
        (fg: any) => fg.ID === linkedFilter.FilterId || fg.ID === suffixedId,
      );
      if (sbMatch) {
        constituent = {
          ...sbMatch,
          Type: sbMatch.Render?.Type,
          DropdownMetaData: sbMatch.Render?.DropdownMetaData || sbMatch.DropdownMetaData,
        } as IFilterConfig;
        break;
      }
    }
  }

  if (constituent?.Type === FilterType.UserMultiSelect && constituent?.IsPrimaryUserFilter) {
    const result: IConstituentApplyResult = {
      key,
      value: customFilterResponse?.UserIds,
      userDropdownSelectType: customFilterResponse?.Type,
    };
    if (isSubBuilderConstituent && linkedFilter.QueryId) {
      result.subBuilderMetadata = {
        QueryId: linkedFilter.QueryId,
        FilterId: linkedFilter.FilterId,
        UserDropdownSelectType: customFilterResponse?.Type,
        Status: constituent?.DropdownMetaData?.UserStatusType || wrapper?.DropdownMetaData?.UserStatusType,
      };
    }
    return result;
  }
  if (constituent?.Type === FilterType.UserMultiSelect) {
    return {
      key,
      value: {
        DropdownMetaData: {
          DropdownSelectType: customFilterResponse?.Type,
          UserStatusType: constituent?.DropdownMetaData?.UserStatusType || wrapper?.DropdownMetaData?.UserStatusType,
        },
        Value: customFilterResponse?.UserIds,
      },
    };
  }
  if (isCustomFilterLOS && !isLargeOptionSetFilter(constituent)) {
    return { key, value: customFilterResponse?.Values || [] };
  }
  return { key, value: customFilterResponse };
};

export const getFilterConfigFromBuilder = (
  builder: Array<IBuilder>,
  filterMetaInformation: IFilterMetaInfo[],
  subBuilders: Array<IBuilder>,
  partitionFilters?: Array<string>,
  customFilterDefinitions?: ICustomFilterDefinition[],
) => {
  const selectedFilter: IFilterConfig[] = [];
  builder?.forEach((builderInfo: IBuilder) => {
    const filterConfig = filterBuilderToFilterConfig(
      builderInfo.Filter,
      filterMetaInformation,
      partitionFilters,
      false,
    );
    selectedFilter.push(...filterConfig);
  });
  if (isValidArrayAndNotEmpty(subBuilders)) {
    subBuilders?.forEach((subBuilder: IBuilder) => {
      const filterConfig = filterBuilderToFilterConfig(
        subBuilder.Filter,
        filterMetaInformation,
        partitionFilters,
        true,
      );
      selectedFilter.push(...filterConfig);
    });
  }
  // For query reports, reconstruction of custom filter wrapper in the filter does not make sense
  if (!isValidArrayAndNotEmpty(subBuilders)) {
    return selectedFilter;
  }
  // Use definitions if available, fall back to legacy reconstruction
  if (isValidArrayAndNotEmpty(customFilterDefinitions)) {
    return buildCustomFiltersFromDefinitions(customFilterDefinitions, selectedFilter, subBuilders);
  }
  return reconstructCustomFilters(selectedFilter);
};

// Generates the report URL on Save and SaveAs action based on isMarvin and IsEmbedded context
export const getSaveSaveAsURL = (isMarvin: boolean, isEmbedded: boolean, reportId: string) => {
  const params = new URLSearchParams(window.location.search);
  const referral = params.get(QueryParamNames.Referral) || `${ListingSectionId.Report}/${SectionItemId.PinnedReports}`;
  let reportUrl = `${window.location.origin}/report/${reportId}?referral=${referral}&${QueryParamNames.ShowToast}=${ShowToastTypes.SAVEAS}`;
  if (isMarvin) {
    reportUrl += '&isMarvin=true';
  }
  if (isEmbedded) {
    reportUrl += '&context=Embedded';
  }

  return reportUrl;
};

export const findValueByKey = (object: Object, key: string) => {
  let result: any;

  function search(obj: ObjModel.Obj) {
    if (obj !== null && typeof obj === 'object') {
      if (Object.hasOwnProperty.call(obj, key)) {
        result = obj[key];
        return;
      }
      Object.keys(obj).forEach((k) => {
        if (typeof obj[k] === 'object' && obj[k] !== null) {
          search(obj[k]);
        }
      });
    }
  }

  search(object);
  return result;
};
export const handleMobileBackBtnNav = (mobileAuthInfo:MobileAuthInfo) => {
  if (mobileAuthInfo.source === SourceTypes.IOS) {
    window.webkit?.messageHandlers?.backButtonTapped?.postMessage(
      JSON.stringify({ exception: 'backButtonTapped' }),
    );
  }
  if (mobileAuthInfo.source === SourceTypes.ANDROID) {
    window.LSQAndroidClient?.backButtonTapped();
  }
};

export const isPaginatedFilter = (filter: { Type?: FilterType; IsLargeOptionSetField?: boolean }): boolean => (filter.Type === FilterType.UserMultiSelect || isLargeOptionSetFilter(filter));

export const isLargeOptionSetFilter = (filter: { Type?: FilterType; IsLargeOptionSetField?: boolean }): boolean => (filter?.IsLargeOptionSetField);

/**
 * Extracts the original alias from a renamed alias.
 * If the alias contains the rename delimiter, returns the part before the delimiter.
 * Otherwise, returns the original alias unchanged.
 * @param alias - The alias that may contain a rename delimiter
 * @returns The original alias without the rename suffix
 * @example
 * getOriginalAlias('mx_LOS::RenamedAlias') // returns 'mx_LOS'
 * getOriginalAlias('mx_LOS') // returns 'mx_LOS'
 */
export const getOriginalAlias = (alias: string) => {
  if (alias.includes(RENAME_DELIMITER)) {
    return alias.split(RENAME_DELIMITER)[0];
  }
  return alias;
};
